import { HTTPResponseError } from "@soft-db/http";
import { Audit, AuditTarget, useApi } from "@soft-db/monitoring-api-client";
import { Breadcrumb, SoftBreadcrumbs } from "@soft-db/ui";
import { PropType } from "vue";
import { RouteLocationRaw } from "vue-router";
import { useHttpError } from "../../composable/http-error";
import { useLocationResolver } from "../../composable/location-resolver";
import { useAuditNameResolver } from "./audit-name-resolver";

export default defineComponent({
	name: "AuditTargetDisplay",
	props: {
		audit: {
			type: Object as PropType<Audit>,
			required: true,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const { resolveTargetName } = useAuditNameResolver();

		const { resolve } = useLocationResolver();

		function getIdFrom(targetType: AuditTarget) {
			const { target, parent } = props.audit;
			if (target?.type === targetType) {
				return target.id;
			}

			if (parent?.type === targetType) {
				return parent.id;
			}

			return null;
		}

		const distId = computed(() => getIdFrom("distributor"));
		const userId = computed(() => getIdFrom("user_account"));
		const stationId = computed(() => getIdFrom("station"));
		const siteId = computed(() => getIdFrom("site"));
		const alertId = computed(() => getIdFrom("alert"));
		const spaceId = computed(() => getIdFrom("space"));
		const reportId = computed(() => getIdFrom("report"));
		const publicPageId = computed(() => getIdFrom("public_page"));
		const vibrationCriteriaId = computed(() => getIdFrom("safe_limit"));
		const weeklyLimitId = computed(() => getIdFrom("weekly_limit"));
		const dynamicImageId = computed(() => getIdFrom("dynamic_image"));

		async function handleNotFoundToNull<T>(req: Promise<T>): Promise<T | null> {
			try {
				return await req;
			} catch (e) {
				if (e instanceof HTTPResponseError && (e.status === 404 || e.status === 403)) {
					return null;
				}

				throw e;
			}
		}

		async function retryOrNull<T>(req: Promise<T>): Promise<T | null> {
			const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

			for (let i = 0; i < 5; i++) {
				try {
					return await req;
				} catch {
					await delay(100 * 2 ** i);
				}
			}

			return null;
		}

		async function makeRequest<T>(fn: () => Promise<T>): Promise<T | null> {
			return retryOrNull(handleNotFoundToNull(fn()));
		}

		// Prevent the default handler to redirect of 403.
		useHttpError((err) => err.markHandled());

		const api = useApi();

		const loading = ref(false);
		const paths = ref<Breadcrumb[]>([]);
		watchEffect(async () => {
			loading.value = true;
			paths.value = [];

			const pathsBuilder: Breadcrumb[] = [];

			if (userId.value) {
				const id = userId.value;
				const user = await makeRequest(() => api.user().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("user_account") },
					{
						text: user ? user.name : t("audit.deleted"),
						// TODO: Update to match new user path
						// to: user ? resolve({ name: "administration-users-id", params: { id: user.id } }) : null,
					}
				);
			}

			if (stationId.value) {
				const id = stationId.value;
				const station = await makeRequest(() => api.station().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("station") },
					{
						text: station ? station.name : t("audit.deleted"),
						to: station ? resolve({ name: "stations-id-index", params: { id: station.id } }) : null,
					}
				);
			}

			if (siteId.value) {
				const id = siteId.value;
				const site = await makeRequest(() => api.site().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("site") },
					{
						text: site ? site.name : t("audit.deleted"),
						to: site ? resolve({ name: "sites-id", params: { id: site.id } }) : null,
					}
				);

				if (alertId.value) {
					const id = alertId.value;
					const alert = await makeRequest(() => api.alert().fetch(id));

					pathsBuilder.push(
						{ text: resolveTargetName("alert") },
						{
							text: alert ? alert.name : t("audit.deleted"),
							to:
								site && alert
									? resolve({
											name: "sites-id-alerts-alertid",
											params: { id: site.id, alertid: alert.id },
										})
									: null,
						}
					);
				}

				if (vibrationCriteriaId.value) {
					const id = vibrationCriteriaId.value;
					const vc = await makeRequest(() => api.vibrationCriterias().fetch(id));

					pathsBuilder.push(
						{ text: resolveTargetName("vibration_criteria") },
						{
							text: vc ? vc.name : t("audit.deleted"),
							to:
								vc && site
									? resolve({
											name: "sites-id-limits",
											params: { id: site.id },
										})
									: null,
						}
					);
				}

				if (weeklyLimitId.value) {
					const id = weeklyLimitId.value;
					const wl = await makeRequest(() => api.weeklyLimit().fetch(id));

					pathsBuilder.push(
						{ text: resolveTargetName("weekly_limit") },
						{
							text: wl ? wl.name : t("audit.deleted"),
							to:
								wl && site
									? resolve({
											name: "sites-id",
											params: { id: site.id },
										})
									: null,
						}
					);
				}

				if (dynamicImageId.value) {
					const id = dynamicImageId.value;
					const dyn = await makeRequest(() => api.dynamicImage().fetch(id));

					pathsBuilder.push(
						{ text: resolveTargetName("dynamic_image") },
						{
							text: dyn ? dyn.name : t("audit.deleted"),
							to:
								dyn && site
									? resolve({
											name: "sites-id",
											params: { id: site.id },
										})
									: null,
						}
					);
				}
			}

			if (distId.value) {
				const id = distId.value;
				const org = await makeRequest(() => api.distributor().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("distributor") },
					{
						text: org ? org.name : t("audit.deleted"),
						to: org
							? resolve({
									name: "distributor-id",
									params: { id: distId.value },
								})
							: null,
					}
				);
			}

			if (spaceId.value) {
				const id = spaceId.value;
				const space = await makeRequest(() => api.space().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("space") },
					{
						text: space ? space.name : t("audit.deleted"),
						to: space
							? resolve({
									name: "spaces-id",
									params: { id: space.id },
								})
							: null,
					}
				);
			}

			if (reportId.value) {
				const id = reportId.value;
				const report = await makeRequest(() => api.report().fetch(id));

				pathsBuilder.push(
					{ text: resolveTargetName("report") },
					{
						text: report ? report.title : t("audit.deleted"),
						to: report
							? resolve({
									name: "reports-id-settings",
									params: { id: report.id },
								})
							: null,
					}
				);
			}

			if (publicPageId.value) {
				const id = publicPageId.value;
				const publicPage = await makeRequest(() => api.publicPage().fetch(id));

				let to: RouteLocationRaw | null = null;
				if (publicPage?.scope.type === "space") {
					to = {
						name: "spaces-id-public-pages-pageid",
						params: {
							id: publicPage.scope.id,
							pageid: publicPage.id,
						},
					};
				} else if (publicPage?.scope.type === "distributor") {
					to = {
						name: "distributor-id-public-pages-pageid",
						params: {
							id: publicPage.scope.id,
							pageid: publicPage.id,
						},
					};
				}

				pathsBuilder.push(
					{ text: resolveTargetName("public_page") },
					{
						text: publicPage ? publicPage.name : t("audit.deleted"),
						to,
					}
				);
			}

			paths.value = pathsBuilder;
			loading.value = false;
		});

		return () => !loading.value && <SoftBreadcrumbs class="py-0" breadcrumbs={paths.value} />;
	},
});
